import { defineStore } from 'pinia'
import { apiService } from '@/services/api'
import Papa from 'papaparse'
import xml2js from 'xml2js'

// xml2js.Builderインスタンスを作成（前サービスと同じ設定）
const parser = new xml2js.Parser({ explicitArray: true, ignoreAttrs: true })
const builder = new xml2js.Builder({
  xmldec: { standalone: null, encoding: 'UTF-8' },
  renderOpts: { pretty: false },
})

export const useEgovApplicationStore = defineStore('egovApplication', {
  state: () => ({
    // 基本データ
    publicProcedureInfoObject: {},
    kouseiObject: {},
    dataObjects: {},
    formInfo: [],
    companies: {},
    checklists: { xmlns: true },
    publicOrgInfo: [],

    // 現在の状態
    currentFormID: null,
    isPreview: false,
    isChecked: false,

    // 申請関連データ
    attachedFiles: [],
    applyDataList: [],
    applyFlagList: {},
    // 申請者情報・連絡先情報（v-model用）
    applicantInfo: {
      lastName: '',
      firstName: '',
      lastKana: '',
      firstKana: '',
      title: '',
      companyName: '',
      companyNameKana: '',
      division: '',
      divisionKana: '',
      postalCode: ['', ''],
      address: '',
      addressKana: '',
      phone: ['', '', ''],
      fax: ['', '', ''],
      email: '',
    },
    contactInfo: {
      lastName: '',
      firstName: '',
      lastKana: '',
      firstKana: '',
      title: '',
      companyName: '',
      companyNameKana: '',
      division: '',
      divisionKana: '',
      postalCode: ['', ''],
      address: '',
      addressKana: '',
      phone: ['', '', ''],
      fax: ['', '', ''],
      email: '',
    },

    // 提出先情報
    major: null,
    medium: null,
    minor: null,

    // 申請者・連絡先情報（computed用の元データ）
    applicationInfo: {
      shinseisha: {},
      renrakusaki: {},
    },

    // 手続きデータ
    procList: [],
    activeCode: '', // kijun_2.6など
    currentProc: null, // 現在の手続き情報
    currentAgreementId: null, // 現在の36協定ID
    // 再申請フラグ
    isResubmission: false,
    initialArriveId: null,
  }),

  actions: {
    // 手続きデータを取得
    async getEgovMetadata() {
      try {
        // e-Gov手続き一覧取得
        const response = await apiService.get(`api/egov-metadata`)
        // 手続きリストを取得してJSON展開
        this.procList = response.procIdMapJson
        this.activeCode = response.code
        return true
      } catch (error) {
        return false
      }
    },

    // 申請者情報取得API呼び出し
    async getApplicantInfo(branchId) {
      try {
        const response = await apiService.get(`api/egov-applicant-info/${branchId}`, {}, { showErrorDialog: false })
        this.applicantInfo = response.applicantInfo || {}
        return true
      } catch (error) {
        // 空データで継続
        this.applicantInfo = {
          lastName: '',
          firstName: '',
          lastKana: '',
          firstKana: '',
          title: '',
          companyName: '',
          companyNameKana: '',
          division: '',
          divisionKana: '',
          postalCode: ['', ''],
          address: '',
          addressKana: '',
          phone: ['', '', ''],
          fax: ['', '', ''],
          email: '',
        }
        return false
      }
    },

    // 連絡先情報取得API呼び出し
    async getContactInfo() {
      try {
        const response = await apiService.get(
          `api/egov-contact-info/`,
          {},
          {
            showErrorDialog: false,
          }
        )
        this.contactInfo = response.contactInfo || {}
        return true
      } catch (error) {
        // 空データで継続
        this.contactInfo = {
          lastName: '',
          firstName: '',
          lastKana: '',
          firstKana: '',
          title: '',
          companyName: '',
          companyNameKana: '',
          division: '',
          divisionKana: '',
          postalCode: ['', ''],
          address: '',
          addressKana: '',
          phone: ['', '', ''],
          fax: ['', '', ''],
          email: '',
        }
        return false
      }
    },

    // 提出先情報API呼び出し（両方のIDを送信）
    async getSubmissionInfo(branchId, agreementId) {
      try {
        const data = {
          branchId: branchId || null,
          agreementId: agreementId || null,
        }

        const response = await apiService.get(`api/egov-submission-info`, data, {
          showErrorDialog: false,
        })
        this.major = response.major || null
        this.medium = response.medium || null
        this.minor = response.minor || null

        // 36協定IDをstateに保存
        this.currentAgreementId = agreementId || null

        return true
      } catch (error) {
        // 空データで継続
        this.major = null
        this.medium = null
        this.minor = null
        this.currentAgreementId = null
        return false
      }
    },

    // getText関数をVue側から移植
    async getText(name) {
      try {
        let filePath

        if (name.includes('kousei/kousei.xml')) {
          filePath = `/storage/egovdata/kousei/kousei.xml`
        } else if (name.includes('PublicOrgInfo.csv')) {
          filePath = `/storage/egovdata/laborStandard/${this.activeCode}/PublicOrgInfo.csv`
        } else if (name.startsWith(this.activeCode)) {
          filePath = `/storage/egovdata/laborStandard/${name}`
        } else {
          filePath = `/storage/egovdata/laborStandard/${this.activeCode}/${name}`
        }

        const response = await fetch(filePath)

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const content = await response.text()
        return content
      } catch (error) {
        return ''
      }
    },

    // PublicOrgInfo読み込み（PapaParseを使用、シンプル版）
    async loadPublicOrgInfo() {
      try {
        const csvContent = await this.getText('PublicOrgInfo.csv')

        // PapaParseでCSVを解析
        const parsedCsv = Papa.parse(csvContent, {
          header: false,
          skipEmptyLines: true,
          dynamicTyping: false,
          delimiter: ',',
          quoteChar: '"',
        })

        if (parsedCsv.errors.length > 0) {
        }

        // "署"のデータのみを抽出してシンプルな構造に
        this.publicOrgInfo = parsedCsv.data
          .filter(row => row.length >= 4 && row[1] === '署')
          .map(row => {
            const parts = row[3].split(',')
            return {
              id: row[2],
              major: parts[0]?.trim() || '',
              medium: parts[1]?.trim() || '',
              minor: parts[2]?.trim() || '', // 将来の小分類用
            }
          })
      } catch (error) {
        return false
      }
      return true
    },

    // 構成情報読み込み
    async loadKouseiInfo() {
      try {
        const kousei = await this.getText('kousei/kousei.xml')
        if (!kousei) {
          return false
        }
        // this.xmlToObject を this.parseXml に変更
        this.kouseiObject = await this.parseXml(kousei)
        this.kouseiObject['DataRoot']['その他'][0]['法人番号'][0] = '' // 初期値削除
      } catch (error) {
        return false
      }
      return true
    },

    // 手続き情報読み込み（getText引数を削除し、this.getTextを使用）
    async loadProcedureInfo(type) {
      // this.procListから該当する手続きを検索
      const targetProc = this.procList.find(proc => proc.code === type)

      if (!targetProc) {
        return false
      }
      this.currentProc = targetProc

      const publicProcedureInfo = await this.getText(`${this.activeCode}/${targetProc.code.slice(0, -3)}/PublicProcedureInfo.xml`)

      this.publicProcedureInfoObject = await this.parseXml(publicProcedureInfo)

      const procInfo = this.publicProcedureInfoObject.AdminProcInfo.ProcInfo[0]

      // 添付ファイル情報設定
      if (procInfo.AttachedFileInfo) {
        this.attachedFiles = procInfo.AttachedFileInfo.map(item => ({
          Title: item.AttachedFileName[0],
          Filename: '',
          Data: '',
        }))
      }

      // フォーム情報設定
      this.formInfo = procInfo.FormInfo

      // フォームデータ読み込み
      for (const item of this.formInfo) {
        const path = `${this.publicProcedureInfoObject.AdminProcInfo.AdminProcID[0]}/${item.FormId[0]}`
        const XMLFile = await this.getText(`${this.activeCode}/${path}/${item.XMLFile[0]}`)
        // this.xmlToObject を this.parseXml に変更
        this.dataObjects[item.FormId] = await this.parseXml(XMLFile)
      }

      // ApplyData用の申請フラグを作成
      this.formInfo.forEach(item => {
        this.applyFlagList[item.FormId[0]] = false
      })

      // 初期フォーム選択を設定（追加）
      this.initializeCurrentForm()
      return true
    },

    // 既存申請を再申請用に読み込む
    async loadApplicationForResubmission(id) {
      if (!id) {
        // 継続
        return true
      }
      try {
        // 申請データJSONをそのまま使いたいので、JSONのキーを変換しない
        const response = await apiService.get(
          `api/egov/applications/${id}`,
          {},
          {
            caseConversion: { responseToCamelCase: false },
          }
        )
        if (!response.application_json) {
          return false
        }

        await this.loadSaveData(response.application_json)
        this.currentAgreementId = response.agreement_id || null
        this.isResubmission = true
        this.initialArriveId = response.initial_arrive_id || null
        return true
      } catch (error) {
        return false
      }
    },

    // 申請者情報初期化（APIベース版）
    async initShinseisha(branchId) {
      const success = await this.getApplicantInfo(branchId)

      if (!success) {
        return false
      }

      // 前回データがあるかチェックして上書き
      const prevKouseiShinseisha = this.kouseiObject['DataRoot']?.['構成情報']?.[0]?.['管理情報']?.[0]?.['申請者連絡先情報']?.[0]?.['申請者情報']?.[0]

      if (prevKouseiShinseisha) {
        // 前回データがある場合は優先して使用
        if (prevKouseiShinseisha['氏名']?.[0] && prevKouseiShinseisha['氏名'][0] !== '') {
          this.applicantInfo.name = prevKouseiShinseisha['氏名'][0]
        }
        if (prevKouseiShinseisha['氏名フリガナ']?.[0] && prevKouseiShinseisha['氏名フリガナ'][0] !== '') {
          this.applicantInfo.nameKana = prevKouseiShinseisha['氏名フリガナ'][0]
        }
        if (prevKouseiShinseisha['役職']?.[0] && prevKouseiShinseisha['役職'][0] !== '') {
          this.applicantInfo.title = prevKouseiShinseisha['役職'][0]
        }
        if (prevKouseiShinseisha['法人団体名']?.[0] && prevKouseiShinseisha['法人団体名'][0] !== '') {
          this.applicantInfo.companyName = prevKouseiShinseisha['法人団体名'][0]
        }
        if (prevKouseiShinseisha['法人団体名フリガナ']?.[0] && prevKouseiShinseisha['法人団体名フリガナ'][0] !== '') {
          this.applicantInfo.companyNameKana = prevKouseiShinseisha['法人団体名フリガナ'][0]
        }
        if (prevKouseiShinseisha['部門名']?.[0] && prevKouseiShinseisha['部門名'][0] !== '') {
          this.applicantInfo.division = prevKouseiShinseisha['部門名'][0]
        }
        if (prevKouseiShinseisha['部門名フリガナ']?.[0] && prevKouseiShinseisha['部門名フリガナ'][0] !== '') {
          this.applicantInfo.divisionKana = prevKouseiShinseisha['部門名フリガナ'][0]
        }
        if (prevKouseiShinseisha['郵便番号']?.[0] && prevKouseiShinseisha['郵便番号'][0] !== '') {
          this.applicantInfo.postalCode = prevKouseiShinseisha['郵便番号'][0]
        }
        if (prevKouseiShinseisha['住所']?.[0] && prevKouseiShinseisha['住所'][0] !== '') {
          this.applicantInfo.address = prevKouseiShinseisha['住所'][0]
        }
        if (prevKouseiShinseisha['住所フリガナ']?.[0] && prevKouseiShinseisha['住所フリガナ'][0] !== '') {
          this.applicantInfo.addressKana = prevKouseiShinseisha['住所フリガナ'][0]
        }
        if (prevKouseiShinseisha['電話番号']?.[0] && prevKouseiShinseisha['電話番号'][0] !== '') {
          this.applicantInfo.phone = prevKouseiShinseisha['電話番号'][0]
        }
        if (prevKouseiShinseisha['FAX番号']?.[0] && prevKouseiShinseisha['FAX番号'][0] !== '') {
          this.applicantInfo.fax = prevKouseiShinseisha['FAX番号'][0]
        }
        if (prevKouseiShinseisha['電子メールアドレス']?.[0] && prevKouseiShinseisha['電子メールアドレス'][0] !== '') {
          this.applicantInfo.email = prevKouseiShinseisha['電子メールアドレス'][0]
        }
      }
      return true
    },

    // 連絡先情報初期化（APIベース版）
    async initRenrakusaki() {
      const success = await this.getContactInfo()

      if (!success) {
        return false
      }

      // 前回データがあるかチェックして上書き
      const prevKouseiRenrakusaki = this.kouseiObject['DataRoot']?.['構成情報']?.[0]?.['管理情報']?.[0]?.['申請者連絡先情報']?.[0]?.['連絡先情報']?.[0]

      if (prevKouseiRenrakusaki) {
        // 前回データがある場合は優先して使用
        if (prevKouseiRenrakusaki['氏名']?.[0] && prevKouseiRenrakusaki['氏名'][0] !== '') {
          // 氏名を姓名に分割
          const fullName = prevKouseiRenrakusaki['氏名'][0]
          const nameParts = fullName.split('　')
          this.contactInfo.lastName = nameParts[0] || ''
          this.contactInfo.firstName = nameParts[1] || ''
        }
        if (prevKouseiRenrakusaki['氏名フリガナ']?.[0] && prevKouseiRenrakusaki['氏名フリガナ'][0] !== '') {
          // 氏名フリガナを姓名フリガナに分割
          const fullNameKana = prevKouseiRenrakusaki['氏名フリガナ'][0]
          const nameKanaParts = fullNameKana.split('　')
          this.contactInfo.lastKana = nameKanaParts[0] || ''
          this.contactInfo.firstKana = nameKanaParts[1] || ''
        }
        if (prevKouseiRenrakusaki['役職']?.[0] && prevKouseiRenrakusaki['役職'][0] !== '') {
          this.contactInfo.title = prevKouseiRenrakusaki['役職'][0]
        }
        if (prevKouseiRenrakusaki['法人団体名']?.[0] && prevKouseiRenrakusaki['法人団体名'][0] !== '') {
          this.contactInfo.companyName = prevKouseiRenrakusaki['法人団体名'][0]
        }
        if (prevKouseiRenrakusaki['法人団体名フリガナ']?.[0] && prevKouseiRenrakusaki['法人団体名フリガナ'][0] !== '') {
          this.contactInfo.companyNameKana = prevKouseiRenrakusaki['法人団体名フリガナ'][0]
        }
        if (prevKouseiRenrakusaki['部門名']?.[0] && prevKouseiRenrakusaki['部門名'][0] !== '') {
          this.contactInfo.division = prevKouseiRenrakusaki['部門名'][0]
        }
        if (prevKouseiRenrakusaki['部門名フリガナ']?.[0] && prevKouseiRenrakusaki['部門名フリガナ'][0] !== '') {
          this.contactInfo.divisionKana = prevKouseiRenrakusaki['部門名フリガナ'][0]
        }
        if (prevKouseiRenrakusaki['郵便番号']?.[0] && prevKouseiRenrakusaki['郵便番号'][0] !== '') {
          this.contactInfo.postalCode = prevKouseiRenrakusaki['郵便番号'][0]
        }
        if (prevKouseiRenrakusaki['住所']?.[0] && prevKouseiRenrakusaki['住所'][0] !== '') {
          this.contactInfo.address = prevKouseiRenrakusaki['住所'][0]
        }
        if (prevKouseiRenrakusaki['住所フリガナ']?.[0] && prevKouseiRenrakusaki['住所フリガナ'][0] !== '') {
          this.contactInfo.addressKana = prevKouseiRenrakusaki['住所フリガナ'][0]
        }
        if (prevKouseiRenrakusaki['電話番号']?.[0] && prevKouseiRenrakusaki['電話番号'][0] !== '') {
          this.contactInfo.phone = prevKouseiRenrakusaki['電話番号'][0]
        }
        if (prevKouseiRenrakusaki['FAX番号']?.[0] && prevKouseiRenrakusaki['FAX番号'][0] !== '') {
          this.contactInfo.fax = prevKouseiRenrakusaki['FAX番号'][0]
        }
        if (prevKouseiRenrakusaki['電子メールアドレス']?.[0] && prevKouseiRenrakusaki['電子メールアドレス'][0] !== '') {
          this.contactInfo.email = prevKouseiRenrakusaki['電子メールアドレス'][0]
        }
      }
      return true
    },

    // 初期フォーム選択の設定
    initializeCurrentForm() {
      if (this.formInfo.length > 0 && !this.currentFormID) {
        this.currentFormID = this.formInfo[0].FormId[0]
      }
    },

    // フォーム選択
    setCurrentForm(formId) {
      if (!formId || !this.formInfo.find(item => item.FormId[0] === formId)) {
        return false
      }
      this.currentFormID = formId
      return true
    },

    // プレビュー切り替え
    togglePreview() {
      this.isPreview = !this.isPreview
    },

    // 提出先設定
    setSubmissionTarget(majorValue, mediumValue, minorValue) {
      this.major = majorValue
      this.medium = mediumValue
      this.minor = minorValue
      this.isChecked = false
    },

    // 添付ファイル設定
    setAttachedFile(index, filename, data) {
      if (this.attachedFiles[index]) {
        this.attachedFiles[index].Filename = filename
        this.attachedFiles[index].Data = data
        this.isChecked = false
      }
    },
    // 添付ファイルクリア
    clearAttachedFile(index) {
      if (this.attachedFiles[index]) {
        this.attachedFiles[index].Filename = ''
        this.attachedFiles[index].Data = ''
        this.isChecked = false
      }
    },

    // フォームデータ更新（XSL変換後のフォーム入力値更新）
    updateFormData(formId, path, value) {
      if (!this.dataObjects[formId]) return

      const DataTarget = this.dataObjects[formId]['DataRoot'][Object.keys(this.dataObjects[formId]['DataRoot']).find(key => !['様式ID', '様式バージョン', 'STYLESHEET', '様式コピー情報', 'Doctype'].includes(key))][0]

      // パス解析してデータ更新
      const pathArray = path.split('/')
      if (!DataTarget[pathArray[0]]) return

      // 動的にネストされたオブジェクトに値を設定
      let current = DataTarget
      for (let i = 0; i < pathArray.length - 1; i++) {
        if (current[pathArray[i]] && current[pathArray[i]][0]) {
          current = current[pathArray[i]][0]
        } else {
          return
        }
      }

      const lastKey = pathArray[pathArray.length - 1]
      if (current[lastKey]) {
        current[lastKey] = [value]
      }

      // 申請フラグを立てる
      this.applyFlagList[formId] = true
      this.isChecked = false
    },

    // 申請データ作成
    createApplicationData() {
      // API呼び出し前にkouseiObjectを最新状態に同期
      this.syncApplicantInfoToKousei()
      this.syncContactInfoToKousei()

      const procInfo = this.publicProcedureInfoObject.AdminProcInfo.ProcInfo[0]
      // 提出先情報設定
      const teisyutsusaki = [this.major, this.medium, this.minor].filter(item => item).join(',')

      // 提出先識別子を取得
      let orgId = ''

      // まずprocInfo.OrgInfoから検索
      const orgFromProcInfo = procInfo.OrgInfo?.find(item => item.OrgName?.[0] === this.medium)
      if (orgFromProcInfo && orgFromProcInfo.OrgId?.[0]) {
        orgId = orgFromProcInfo.OrgId[0]
      } else {
        // procInfo.OrgInfoになければpublicOrgInfoから検索
        const orgFromPublicInfo = this.publicOrgInfo.find(item => item.major === this.major && item.medium === this.medium && (this.minor ? item.minor === this.minor : true))

        if (orgFromPublicInfo && orgFromPublicInfo.id) {
          orgId = orgFromPublicInfo.id
        } else {
          // それでも見つからない場合はprocInfo.OrgInfoの最初の要素のOrgIdを使用
          if (procInfo.OrgInfo && procInfo.OrgInfo.length > 0 && procInfo.OrgInfo[0].OrgId && procInfo.OrgInfo[0].OrgId[0]) {
            orgId = procInfo.OrgInfo[0].OrgId[0]
          } else {
            // 提出先識別子が取得できない場合はエラーとして処理
            const errorMessage = '提出先識別子を取得できませんでした。提出先を正しく選択してください。'
            throw new Error(errorMessage) // エラーをスローして処理を中断
          }
        }
      }

      // 提出先情報を設定
      this.kouseiObject['DataRoot']['構成情報'][0]['提出先情報'][0]['提出先名称'][0] = teisyutsusaki
      this.kouseiObject['DataRoot']['構成情報'][0]['提出先情報'][0]['提出先識別子'][0] = orgId

      // 管理情報設定
      this.kouseiObject['DataRoot']['構成情報'][0]['管理情報'][0]['手続番号'][0]['受付行政機関ID'][0] = this.currentProc?.organizationId || '100495'
      this.kouseiObject['DataRoot']['構成情報'][0]['管理情報'][0]['手続番号'][0]['手続ID'][0] = procInfo.ProcID[0]
      this.kouseiObject['DataRoot']['構成情報'][0]['管理情報'][0]['手続名称'][0] = procInfo.ProcName[0]
      const management = this.kouseiObject['DataRoot']['構成情報'][0]['管理情報'][0]

      // isCheckedがtrueの場合のみ申請種別を設定
      if (this.isResubmission) {
        if (this.initialArriveId) {
          management['初回受付番号'] = [this.initialArriveId]
        }
        management['申請種別'][0] = '再提出'
      } else {
        management['申請種別'][0] = '新規申請'
      }

      // 添付書類属性情報
      if (this.attachedFiles.length > 0) {
        this.kouseiObject['DataRoot']['構成情報'][0]['添付書類属性情報'] = this.attachedFiles.map(item => ({
          添付種別: ['添付'],
          添付書類名称: [item.Title],
          添付書類ファイル名称: [item.Filename],
          提出情報: ['1'],
        }))
      }
      // 申請書属性情報
      this.kouseiObject['DataRoot']['構成情報'][0]['申請書属性情報'] = []

      // 申請データリスト作成
      this.applyDataList = this.formInfo
        .filter(item => this.applyFlagList[item.FormId[0]])
        .map(item => {
          // 申請者属性情報
          this.kouseiObject['DataRoot']['構成情報'][0]['申請書属性情報'].push({
            申請書様式ID: [item.FormId[0]],
            申請書様式バージョン: [item.FormVersion[0].padStart(4, '0')],
            申請書様式名称: [item.FormName[0]],
            申請書ファイル名称: [item.XMLFile[0]],
          })

          return {
            FormatId: item.FormId[0],
            Version: item.FormVersion[0].padStart(4, '0'),
            Name: item.FormName[0],
            Filename: item.XMLFile[0],
            DataXml: this.buildXml(this.dataObjects[item.FormId[0]]).replace('<DataRoot>', `<?xml-stylesheet href="${this.dataObjects[item.FormId[0]]['DataRoot']['STYLESHEET'][0]}" type="text/xsl"?>\n<DataRoot>`),
          }
        })

      this.kouseiObject['DataRoot']['構成情報'][0]['$'] = { ID: '構成情報' }

      // 法人番号を設定（法人番号がある場合のみ）
      if (this.kouseiObject['DataRoot']['その他'] && this.kouseiObject['DataRoot']['その他'][0] && this.kouseiObject['DataRoot']['その他'][0]['法人番号']) {
        this.kouseiObject['DataRoot']['その他'][0]['法人番号'][0] = this.applicantInfo.corporateNumber || ''
      }

      // xml2js.Builderを使用してXMLを生成
      const kouseiXml = this.buildXml(this.kouseiObject).replace('<DataRoot>', `<?xml-stylesheet href="${this.kouseiObject['DataRoot']['STYLESHEET'][0]}" type="text/xsl"?>\n<DataRoot>`)

      return {
        KouseiXml: kouseiXml,
        ApplyData: this.applyDataList,
        AttachedFiles: this.attachedFiles,
        ProcId: procInfo.ProcID[0],
      }
    },

    // ZIPファイルを作成してBase64エンコードする
    async createZipFileBase64(data) {
      try {
        const JSZip = (await import('jszip')).default
        const zip = new JSZip()

        // procIdからフォルダを作成
        const procId = this.currentProcInfo.ProcID?.[0] || this.publicProcedureInfoObject.AdminProcInfo?.ProcInfo?.[0]?.ProcID?.[0]
        if (!procId) {
          return null
        }

        // procIdフォルダを作成
        const folder = zip.folder(procId)

        // 構成管理XMLファイルを追加（フォルダ内に）
        if (data.kouseiXml) {
          folder.file('kousei.xml', data.kouseiXml)
        }

        // 申請書XMLファイルを追加（フォルダ内に）
        if (data.applyData && data.applyData.length > 0) {
          data.applyData.forEach(item => {
            folder.file(item.Filename, item.DataXml)
          })
        }

        // 添付ファイルを追加（フォルダ内に）
        if (data.attachedFiles && data.attachedFiles.length > 0) {
          data.attachedFiles.forEach(item => {
            if (item.Data && item.Filename) {
              folder.file(item.Filename, item.Data, { base64: true })
            } else {
            }
          })
        }

        // ZIPファイルを生成してBase64エンコード
        const zipBlob = await zip.generateAsync({ type: 'base64' })
        return zipBlob
      } catch (error) {
        return null
      }
    },

    // 入力チェック実行
    async checkApplication() {
      try {
        // 申請データ作成
        const data = this.createApplicationData()
        if (!data) {
          return null
        }

        // 添付ファイルのDataをb64エンコード
        const attachedFilesEncoded = this.attachedFiles.map(item => ({
          ...item,
          Data: this.encodeText(item.Data),
        }))

        // ZIPファイル作成用のデータ準備
        const zipData = {
          kouseiXml: data.KouseiXml,
          applyData: data.ApplyData,
          attachedFiles: attachedFilesEncoded,
        }

        // ZIPファイルを作成してBase64エンコード
        const zipFileBase64 = await this.createZipFileBase64(zipData)
        if (!zipFileBase64) {
          return { success: false, error: 'ZIPファイルの作成に失敗しました' }
        }

        // e-Gov形式チェックAPI呼び出し（入力チェック時は保存データは送信しない）
        const response = await apiService.post(
          'api/egov/check-application',
          {
            procId: data.ProcId,
            sendFile: zipFileBase64,
            fileName: this.generateZipFileName(),
          },
          {
            showErrorDialog: false,
          }
        )

        this.isChecked = true
        return response.message
      } catch (error) {
        throw error
      }
    },

    // 申請実行
    async submitApplication() {
      try {
        // 申請データ作成
        const data = this.createApplicationData()
        if (!data) {
          return null
        }

        // 添付ファイルのDataをb64エンコード
        const attachedFilesEncoded = this.attachedFiles.map(item => ({
          ...item,
          Data: this.encodeText(item.Data),
        }))

        // ZIPファイル作成用のデータ準備
        const zipData = {
          kouseiXml: data.KouseiXml,
          applyData: data.ApplyData,
          attachedFiles: attachedFilesEncoded,
        }

        // ZIPファイルを作成してBase64エンコード
        const zipFileBase64 = await this.createZipFileBase64(zipData)
        if (!zipFileBase64) {
          return null
        }

        // e-Gov申請API呼び出し（保存データも一緒に送信）
        const response = await apiService.post(
          'api/egov/submit-application',
          {
            procId: data.ProcId,
            sendFile: zipFileBase64,
            fileName: this.generateZipFileName(),
            agreementId: this.currentAgreementId || '', // 保存済みの36協定IDを使用
            saveDataJson: JSON.stringify(data),
          },
          {
            showErrorDialog: false,
          }
        )

        return response.message
      } catch (error) {
        throw error
      }
    },

    // 保存データ生成
    generateSaveData() {
      const data = this.createApplicationData()
      return data ? JSON.stringify(data) : null
    },

    // 保存データ読み込み
    async loadSaveData(jsonData) {
      try {
        const json = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData

        if (json.ProcId !== this.publicProcedureInfoObject.AdminProcInfo.ProcInfo[0].ProcID[0]) {
          return { success: false, error: '申請書の種類が異なります。' }
        }

        if (json.KouseiXml) {
          this.kouseiObject = await this.parseXml(json.KouseiXml)
          const targets = this.kouseiObject['DataRoot']['構成情報'][0]['提出先情報'][0]['提出先名称'][0].split(',')
          this.major = targets[0] || null
          this.medium = targets[1] || null
          this.minor = targets[2] || null

          // KouseiXmlから申請者情報と連絡先情報を復元
          this.syncKouseiToApplicantInfo()
          this.syncKouseiToContactInfo()
        }

        if (json.ApplyData) {
          for (const item of json.ApplyData) {
            this.dataObjects[item.FormatId] = await this.parseXml(item.DataXml)
            this.applyFlagList[item.FormatId] = true
          }
        }

        if (json.AttachedFiles) {
          this.attachedFiles = json.AttachedFiles
        }

        this.isChecked = false
        return { success: true }
      } catch (error) {
        return { success: false, error: 'ファイルの読み込みに失敗しました。' }
      }
    },

    // バリデーション
    validateApplicationData() {
      // バリデーション前にkouseiObjectを最新状態に同期
      this.syncApplicantInfoToKousei()
      this.syncContactInfoToKousei()
      const errors = []

      // 申請者情報チェック
      const checkKeys = ['氏名', '氏名フリガナ', '郵便番号', '住所', '住所フリガナ', '電話番号', '電子メールアドレス']
      const shinseisha = this.shinseisha
      const renrakusaki = this.renrakusaki

      checkKeys.forEach(key => {
        if (!shinseisha[key] || !shinseisha[key][0]) {
          errors.push(`申請者情報の${key}が入力されていません。`)
        }
        if (!renrakusaki[key] || !renrakusaki[key][0]) {
          errors.push(`連絡先情報の${key}が入力されていません。`)
        }
      })

      // 提出先チェック
      if (!this.major || !this.medium) {
        errors.push('提出先情報が選択されていません。')
      }

      // 添付ファイルチェック
      if (this.attachedFiles.some(item => !item.Data)) {
        errors.push('必要な書類が添付されていません。')
      }

      // TODO: API実装後に以下のバリデーションを追加
      // - 電話番号形式チェック（半角数字とハイフン）: shinseisha['電話番号'][0].match(/^[0-9-]+$/)
      // - フリガナ形式チェック（全角カタカナと全角スペース）: ['氏名フリガナ', '法人団体名フリガナ', '部門名フリガナ', '住所フリガナ'].match(/^[\u30A0-\u30FF　]+$/)
      // - 氏名の全角スペースチェック: ['氏名', '氏名フリガナ'].includes('　')
      // - 郵便番号・住所の前処理（ハイフン除去、スペース除去）をバリデーション前に実行

      return errors
    },

    // ユーティリティメソッド

    // UTF-16エンコード後にBase64エンコード
    encodeText(text) {
      const utf16Bytes = encodeURIComponent(text).replace(/%([0-9A-F]{2})/g, (_, p1) => String.fromCharCode(`0x${p1}`))
      return btoa(utf16Bytes)
    },

    // 日付時刻文字列取得
    getDateTimeString() {
      const date = new Date()
      return `${date.getFullYear()}${('0' + (date.getMonth() + 1)).slice(-2)}${('0' + date.getDate()).slice(-2)}${('0' + date.getHours()).slice(-2)}${('0' + date.getMinutes()).slice(-2)}${('0' + date.getSeconds()).slice(-2)}`
    },

    // applicantInfoをkouseiObjectに同期
    syncApplicantInfoToKousei() {
      if (!this.kouseiObject['DataRoot']) return

      const data = {
        氏名: [this.formatFullName(this.applicantInfo.lastName, this.applicantInfo.firstName)],
        氏名フリガナ: [this.formatFullName(this.applicantInfo.lastKana, this.applicantInfo.firstKana)],
        役職: [this.applicantInfo.title || ''],
        法人団体名: [this.applicantInfo.companyName || ''],
        法人団体名フリガナ: [this.applicantInfo.companyNameKana || ''],
        部門名: [this.applicantInfo.division || ''],
        部門名フリガナ: [this.applicantInfo.divisionKana || ''],
        郵便番号: [this.formatPostalCodeForXml(this.applicantInfo.postalCode)],
        住所: [this.applicantInfo.address || ''],
        住所フリガナ: [this.applicantInfo.addressKana || ''],
        電話番号: [this.formatPhoneNumber(this.applicantInfo.phone)],
        FAX番号: [this.formatPhoneNumber(this.applicantInfo.fax)],
        電子メールアドレス: [this.applicantInfo.email || ''],
      }

      this.kouseiObject['DataRoot']['構成情報'][0]['管理情報'][0]['申請者連絡先情報'][0]['申請者情報'][0] = data
    },

    // contactInfoをkouseiObjectに同期
    syncContactInfoToKousei() {
      if (!this.kouseiObject['DataRoot']) return

      const data = {
        氏名: [this.formatFullName(this.contactInfo.lastName, this.contactInfo.firstName)],
        氏名フリガナ: [this.formatFullName(this.contactInfo.lastKana, this.contactInfo.firstKana)],
        役職: [this.contactInfo.title || ''],
        法人団体名: [this.contactInfo.companyName || ''],
        法人団体名フリガナ: [this.contactInfo.companyNameKana || ''],
        部門名: [this.contactInfo.division || ''],
        部門名フリガナ: [this.contactInfo.divisionKana || ''],
        郵便番号: [this.formatPostalCodeForXml(this.contactInfo.postalCode)],
        住所: [this.contactInfo.address || ''],
        住所フリガナ: [this.contactInfo.addressKana || ''],
        電話番号: [this.formatPhoneNumber(this.contactInfo.phone)],
        FAX番号: [this.formatPhoneNumber(this.contactInfo.fax)],
        電子メールアドレス: [this.contactInfo.email || ''],
      }

      this.kouseiObject['DataRoot']['構成情報'][0]['管理情報'][0]['申請者連絡先情報'][0]['連絡先情報'][0] = data
    },

    // kouseiObjectから申請者情報をstateに同期
    syncKouseiToApplicantInfo() {
      if (!this.kouseiObject['DataRoot']) return

      const kouseiShinseisha = this.kouseiObject['DataRoot']['構成情報']?.[0]?.['管理情報']?.[0]?.['申請者連絡先情報']?.[0]?.['申請者情報']?.[0]
      if (!kouseiShinseisha) return

      // 氏名を姓名に分割
      const fullName = kouseiShinseisha['氏名']?.[0] || ''
      const nameParts = fullName.split('　')
      this.applicantInfo.lastName = nameParts[0] || ''
      this.applicantInfo.firstName = nameParts[1] || ''

      // 氏名フリガナを姓名フリガナに分割
      const fullNameKana = kouseiShinseisha['氏名フリガナ']?.[0] || ''
      const nameKanaParts = fullNameKana.split('　')
      this.applicantInfo.lastKana = nameKanaParts[0] || ''
      this.applicantInfo.firstKana = nameKanaParts[1] || ''

      // その他の情報
      this.applicantInfo.title = kouseiShinseisha['役職']?.[0] || ''
      this.applicantInfo.companyName = kouseiShinseisha['法人団体名']?.[0] || ''
      this.applicantInfo.companyNameKana = kouseiShinseisha['法人団体名フリガナ']?.[0] || ''
      this.applicantInfo.division = kouseiShinseisha['部門名']?.[0] || ''
      this.applicantInfo.divisionKana = kouseiShinseisha['部門名フリガナ']?.[0] || ''
      this.applicantInfo.address = kouseiShinseisha['住所']?.[0] || ''
      this.applicantInfo.addressKana = kouseiShinseisha['住所フリガナ']?.[0] || ''
      this.applicantInfo.email = kouseiShinseisha['電子メールアドレス']?.[0] || ''

      // 郵便番号を配列形式に変換（123-4567形式でも対応）
      const postalCode = kouseiShinseisha['郵便番号']?.[0] || ''
      if (postalCode.includes('-')) {
        this.applicantInfo.postalCode = postalCode.split('-')
      } else if (postalCode.length === 7) {
        this.applicantInfo.postalCode = [postalCode.slice(0, 3), postalCode.slice(3)]
      } else {
        this.applicantInfo.postalCode = ['', '']
      }

      // 電話番号を配列形式に変換
      const phone = kouseiShinseisha['電話番号']?.[0] || ''
      this.applicantInfo.phone = phone ? phone.split('-') : ['', '', '']

      // FAX番号を配列形式に変換
      const fax = kouseiShinseisha['FAX番号']?.[0] || ''
      this.applicantInfo.fax = fax ? fax.split('-') : ['', '', '']
    },

    // kouseiObjectから連絡先情報をstateに同期
    syncKouseiToContactInfo() {
      if (!this.kouseiObject['DataRoot']) return

      const kouseiRenrakusaki = this.kouseiObject['DataRoot']['構成情報']?.[0]?.['管理情報']?.[0]?.['申請者連絡先情報']?.[0]?.['連絡先情報']?.[0]
      if (!kouseiRenrakusaki) return

      // 氏名を姓名に分割
      const fullName = kouseiRenrakusaki['氏名']?.[0] || ''
      const nameParts = fullName.split('　')
      this.contactInfo.lastName = nameParts[0] || ''
      this.contactInfo.firstName = nameParts[1] || ''

      // 氏名フリガナを姓名フリガナに分割
      const fullNameKana = kouseiRenrakusaki['氏名フリガナ']?.[0] || ''
      const nameKanaParts = fullNameKana.split('　')
      this.contactInfo.lastKana = nameKanaParts[0] || ''
      this.contactInfo.firstKana = nameKanaParts[1] || ''

      // その他の情報
      this.contactInfo.title = kouseiRenrakusaki['役職']?.[0] || ''
      this.contactInfo.companyName = kouseiRenrakusaki['法人団体名']?.[0] || ''
      this.contactInfo.companyNameKana = kouseiRenrakusaki['法人団体名フリガナ']?.[0] || ''
      this.contactInfo.division = kouseiRenrakusaki['部門名']?.[0] || ''
      this.contactInfo.divisionKana = kouseiRenrakusaki['部門名フリガナ']?.[0] || ''
      this.contactInfo.address = kouseiRenrakusaki['住所']?.[0] || ''
      this.contactInfo.addressKana = kouseiRenrakusaki['住所フリガナ']?.[0] || ''
      this.contactInfo.email = kouseiRenrakusaki['電子メールアドレス']?.[0] || ''

      // 郵便番号を配列形式に変換（123-4567形式でも対応）
      const postalCode = kouseiRenrakusaki['郵便番号']?.[0] || ''
      if (postalCode.includes('-')) {
        this.contactInfo.postalCode = postalCode.split('-')
      } else if (postalCode.length === 7) {
        this.contactInfo.postalCode = [postalCode.slice(0, 3), postalCode.slice(3)]
      } else {
        this.contactInfo.postalCode = ['', '']
      }

      // 電話番号を配列形式に変換
      const phone = kouseiRenrakusaki['電話番号']?.[0] || ''
      this.contactInfo.phone = phone ? phone.split('-') : ['', '', '']

      // FAX番号を配列形式に変換
      const fax = kouseiRenrakusaki['FAX番号']?.[0] || ''
      this.contactInfo.fax = fax ? fax.split('-') : ['', '', '']
    },

    // フォーム関連状態をリセット
    resetFormState() {
      this.currentFormID = null
      this.dataObjects = {}
      this.applyFlagList = {}
      this.formInfo = []
      this.attachedFiles = []
      this.isResubmission = false
      this.initialArriveId = null
    },

    // 姓名を全角スペース区切りに変換
    formatFullName(lastName, firstName) {
      if (!lastName && !firstName) return ''
      return `${lastName || ''}　${firstName || ''}`.trim()
    },

    // 配列形式の電話番号をハイフン区切りに変換
    formatPhoneNumber(phoneArray) {
      if (!Array.isArray(phoneArray)) return ''
      return phoneArray.filter(part => part !== '').join('-')
    },

    // 配列形式の郵便番号をハイフン区切りに変換
    formatPostalCode(postalArray) {
      if (!Array.isArray(postalArray)) return ''
      return postalArray.filter(part => part !== '').join('-')
    },

    // 郵便番号のXML用フォーマット関数を追加
    formatPostalCodeForXml(postalArray) {
      if (!Array.isArray(postalArray)) return ''
      // XML用はハイフンなしで結合
      return postalArray.filter(part => part !== '').join('')
    },

    // ZIPファイル名を生成する
    generateZipFileName() {
      const timestamp = this.getDateTimeString()
      const companyName = this.applicantCompanyName || 'company'
      const procName = this.adminProcName || 'application'
      return `${companyName}_${timestamp}_${procName}.zip`
    },

    // XML変換用メソッドを追加
    async parseXml(xmlString, options = {}) {
      const result = await parser.parseStringPromise(xmlString, options)
      return result
    },

    buildXml(obj) {
      return builder.buildObject(obj)
    },

    // 申請者郵便番号が入力されているかチェック
    isApplicantPostalCodeFilled() {
      return this.applicantInfo.postalCode[0].length === 3 && this.applicantInfo.postalCode[1].length === 4
    },

    // 連絡先郵便番号が入力されているかチェック
    isContactPostalCodeFilled() {
      return this.contactInfo.postalCode[0].length === 3 && this.contactInfo.postalCode[1].length === 4
    },

    // 申請者住所をセット
    setApplicantAddress(address) {
      if (!address) {
        return
      }
      // 住所を設定（e-Gov申請画面は住所が分かれていないので結合）
      this.applicantInfo.address = `${address.prefecture}${address.address1}`
      // 住所カナをセット（半角数字を全角数字に変換）
      if (address.prefectureKana && address.address1Kana) {
        const addressKana = `${address.prefectureKana}${address.address1Kana}`
        this.applicantInfo.addressKana = addressKana.replace(/[0-9]/g, function (s) {
          return String.fromCharCode(s.charCodeAt(0) + 0xfee0)
        })
      }
    },

    // 連絡先住所をセット
    setContactAddress(address) {
      if (!address) {
        return
      }
      // 住所を設定（e-Gov申請画面は住所が分かれていないので結合）
      this.contactInfo.address = `${address.prefecture}${address.address1}`
      // 住所カナをセット（半角数字を全角数字に変換）
      if (address.prefectureKana && address.address1Kana) {
        const addressKana = `${address.prefectureKana}${address.address1Kana}`
        this.contactInfo.addressKana = addressKana.replace(/[0-9]/g, function (s) {
          return String.fromCharCode(s.charCodeAt(0) + 0xfee0)
        })
      }
    },
  },

  getters: {
    // 申請者郵便番号（ハイフン付き）
    applicantPostalCodeWithHyphen: state => {
      return state.applicantInfo.postalCode[0] + '-' + state.applicantInfo.postalCode[1]
    },

    // 連絡先郵便番号（ハイフン付き）
    contactPostalCodeWithHyphen: state => {
      return state.contactInfo.postalCode[0] + '-' + state.contactInfo.postalCode[1]
    },

    // 申請者情報
    shinseisha: state => {
      return Object.keys(state.kouseiObject).length !== 0 ? state.kouseiObject['DataRoot']['構成情報'][0]['管理情報'][0]['申請者連絡先情報'][0]['申請者情報'][0] : {}
    },

    // 連絡先情報
    renrakusaki: state => {
      return Object.keys(state.kouseiObject).length !== 0 ? state.kouseiObject['DataRoot']['構成情報'][0]['管理情報'][0]['申請者連絡先情報'][0]['連絡先情報'][0] : {}
    },

    // 大分類選択肢（労働局一覧）
    majorOptions: state => {
      return [...new Set(state.publicOrgInfo.map(item => item.major))].filter(item => item)
    },

    // 中分類選択肢（選択された労働局に対応する監督署一覧）
    mediumOptions: state => {
      if (!state.major) return []
      return [...new Set(state.publicOrgInfo.filter(item => item.major === state.major).map(item => item.medium))].filter(item => item)
    },

    // 小分類選択肢（選択された労働局・監督署に対応する小分類一覧）
    minorOptions: state => {
      if (!state.major || !state.medium) return []
      return [...new Set(state.publicOrgInfo.filter(item => item.major === state.major && item.medium === state.medium).map(item => item.minor))].filter(item => item)
    },

    // 現在選択中のフォーム情報を取得
    selectedFormInfo: state => {
      if (!state.currentFormID || !state.formInfo.length) return null
      return state.formInfo.find(item => item.FormId[0] === state.currentFormID) || null
    },

    // 手続き名
    adminProcName: state => state.publicProcedureInfoObject.AdminProcInfo?.AdminProcName?.[0] || '',

    // 現在の手続き情報（ProcInfo[0]）
    currentProcInfo: state => state.publicProcedureInfoObject.AdminProcInfo?.ProcInfo?.[0] || {},

    // 申請者の法人団体名
    applicantCompanyName: state => state.shinseisha?.['法人団体名']?.[0] || '',

    // フォーム選択肢（表示用）- 修正版
    formOptions: state => {
      return state.formInfo.map(item => ({
        id: item.FormId?.[0] || '',
        name: item.FormName?.[0] || '',
        value: item.FormId?.[0] || '',
      }))
    },

    // 最初のフォームID
    firstFormId: state => {
      return state.formInfo.length > 0 ? state.formInfo[0].FormId[0] : null
    },
  },
})
